// src/hooks/useShops.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';
import { getAllShops, getShopById, createShop, uploadShopLogo, updateShop, deleteShop, updateShopSearchTags, updateShopFeaturedOfferings } from '../store/services/shopService';
import { v4 as uuidv4 } from 'uuid';
import { useShopStore } from '../store/slices/shopSlice';
import { useEffect } from 'react';
import { useVariantStore } from './useVariants';
import { getShopDetailsByVariantShopId, fetchPaginatedShopsForAdminList,
         fetchFoodAndBeverageInventoryByShopId, fetchProfessionalServicesByShopId, fetchProductVariantsByShopId  } from '../store/services/shopService';
import { updateShopLocationAndContact, updateShopHours, updateShopPolicies, updateShopDeliveryZones, updateShopOnSaleStatus } from '../store/services/shopService';
import { adminUpdateShopCoreConfig, adminUpdateShopOperationalProfile } from '../store/services/shopService';
import { 
    adminUpdateItemDetails, 
    adminDeleteItem, 
    adminCreateNewInventoryItem,
    adminUpdateShopCategories,
    updateShopAvailabilityStatus,
    updateShopAutoAcceptConfig
} from '../store/services/shopService';
import { 
    fetchServicesByShopId, fetchShopItemsForOnSaleToggle, bulkUpdateItemsOnSale
} from '../store/services/shopService';
import { checkShopNameExists, approveShop } from '../store/services/shopService';
import { updateShopDeliveryPolicy } from '../store/services/shopService'; // Adjust path as needed

import toast from 'react-hot-toast'; // Assuming you use react-hot-toast


// Fetch all shops
export const useAllShops = () => {
    return useQuery({
        queryKey: ['shops'],
        queryFn: getAllShops,
    });
};

// Fetch a single shop by ID
export const useShop = (shopId) => {
    return useQuery({
        queryKey: ['shop', shopId],
        queryFn: () => getShopById(shopId),
        enabled: !!shopId,
    });
};


// Upload a single file to ImageKit and track in Supabase
export const useUploadFile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ file, userId, shopId }) => {
            // Replaces Supabase Storage with the new ImageKit service
            return await uploadShopLogo(file, userId, shopId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['shops'] });
        },
    });
};
// Create a new shop
// export const useCreateShop = () => {
//     const queryClient = useQueryClient();
//     return useMutation({
//         // The mutation function now correctly destructures and passes the arguments
//         mutationFn: ({ payload, ownerId }) => createShop(payload, ownerId),
//         onSuccess: () => {
//             queryClient.invalidateQueries({ queryKey: ['shops'] });
//             console.log('Shop created successfully!');
//         },
//         onError: (error) => {
//             console.error('Failed to create shop:', error);
//         },
//     });
// };

// Update an existing shop
export const useUpdateShop = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (shopData) => updateShop(shopData.id, shopData.payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['shops'] });
            queryClient.invalidateQueries({ queryKey: ['shop'] });
            console.log('Shop updated successfully!');
        },
        onError: (error) => {
            console.error('Failed to update shop:', error);
        },
    });
};

// Delete a shop by ID
export const useDeleteShop = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteShop,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['shops'] });
            console.log('Shop deleted successfully!');
        },
        onError: (error) => {
            console.error('Failed to delete shop:', error);
        },
    });
};



////////////////////// For Shop Logins //////////////////////////
// src/hooks/useShops.js
import { 
    searchShopsForLogin, 
    searchProfilesForLogin, 
    fetchUserShopMemberships, 
    fetchShopById,
    fetchShopMembers,
    removeShopMember,
    addShopMember
} from '../store/services/shopService';
// Assuming useAuthStore and supabase are available via imports if needed, 
// but we'll focus on the required hooks.

// --- Search Hooks ---

// NEW: Hook to search shops with a debounce-like effect
export const useSearchShops = (searchTerm) => {
    return useQuery({
        queryKey: ['shopSearch', searchTerm],
        queryFn: () => searchShopsForLogin(searchTerm),
        enabled: !!searchTerm && searchTerm.length >= 2, // Only run query if term is long enough
        staleTime: 1000 * 60, // 1 minute
    });
};

// NEW: Hook to search profiles (users)
export const useSearchProfiles = (searchTerm) => {
    return useQuery({ // <--- This is the first legitimate use of useQuery. If you have an extra import here, it causes the error.
        queryKey: ['profileSearch', searchTerm],
        queryFn: () => searchProfilesForLogin(searchTerm),
        enabled: !!searchTerm && searchTerm.length >= 2, // Only run query if term is long enough
        staleTime: 1000 * 60, // 1 minute
    });
};

// --- Membership Hooks ---

// ../../hooks/useShops.js
// Existing: Hook to add a new member to a shop (from prompt, modified with toast)

// ../../hooks/useShops.js

// ../../hooks/useShops.js

export const useAddShopMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ shopId, userId, role }) => addShopMember(shopId, userId, role),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['shops'] });
            queryClient.invalidateQueries({ queryKey: ['shopMembers', variables.shopId] });
            queryClient.invalidateQueries({ queryKey: ['profile', variables.userId] }); 
            queryClient.invalidateQueries({ queryKey: ['profiles'] }); 

            toast.success(`User added successfully!`);
        },
        onError: (error) => {
            console.error('Add Member Error:', error);
            toast.error(error.message || 'Failed to add member.');
        },
    });
};

export const useRemoveShopMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (membershipId) => removeShopMember(membershipId),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['shopMembers'] });
            if (data?.userId) {
                queryClient.invalidateQueries({ queryKey: ['profile', data.userId] });
            }
            queryClient.invalidateQueries({ queryKey: ['profiles'] });
            
            toast.success('Member successfully removed.');
        },
        onError: (error) => {
            console.error('Remove Member Error:', error);
            toast.error(error.message || 'Failed to remove member.');
        },
    });
};


// NEW: Hook to fetch a user's shops for the login/selection page
export const useUserShopMemberships = (userId) => {
    return useQuery({
        queryKey: ['userMemberships', userId],
        queryFn: () => fetchUserShopMemberships(userId),
        enabled: !!userId,
        staleTime: Infinity, // Memberships shouldn't change often
    });
};

// NEW: Hook to fetch a specific shop's details
export const useShopDetails = (shopId) => {
    return useQuery({
        queryKey: ['shopDetails', shopId],
        queryFn: () => fetchShopById(shopId),
        enabled: !!shopId,
    });
};

// NEW: Hook to fetch existing members of a shop (for the owner's member management page)
export const useShopMembers = (shopId) => {
    return useQuery({
        queryKey: ['shopMembers', shopId],
        queryFn: () => fetchShopMembers(shopId),
        enabled: !!shopId,
        staleTime: 1000 * 10, // 10 seconds
    });
};












/**
 * NEW: Custom hook to fetch the shop details for the selected variant.
 * @returns {{shopInView: object, isLoading: boolean, isError: boolean}}
 */
export const useShopDetailsForVariant = () => {
    const selectedVariant = useVariantStore(s => s.selectedVariant);
    const setShopInView = useShopStore(s => s.setShopInView);
    const shopInView = useShopStore(s => s.shopInView);
    
    const shopId = selectedVariant?.shop_id;

    // Use react-query to fetch the shop details
    const { data, isLoading, isError } = useQuery({
        queryKey: ['shopDetails', shopId],
        queryFn: () => getShopDetailsByVariantShopId(shopId),
        enabled: !!shopId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    // Update the Zustand store whenever the fetched data changes
    useEffect(() => {
        if (data) {
            setShopInView(data);
        }
    }, [data, setShopInView]);

    return { shopInView, isLoading, isError };
};


// src/hooks/useShops.js (Add these new hooks)

// ... existing imports and hooks ...

// NEW: Hook to update a shop's search tags
export const useUpdateSearchTags = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ shopId, tags }) => updateShopSearchTags(shopId, tags),
        onSuccess: (updatedShop) => {
            // Invalidate the specific shop query and the global shops query
            queryClient.invalidateQueries({ queryKey: ['shop', updatedShop.id] });
            queryClient.invalidateQueries({ queryKey: ['shops'] });
            console.log('Search tags updated successfully!');
        },
        onError: (error) => {
            console.error('Failed to update search tags:', error);
        },
    });
};

// NEW: Hook to update a shop's featured offerings
export const useUpdateFeaturedOfferings = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ shopId, offerings }) => updateShopFeaturedOfferings(shopId, offerings),
        onSuccess: (updatedShop) => {
            // Invalidate the specific shop query and the global shops query
            queryClient.invalidateQueries({ queryKey: ['shop', updatedShop.id] });
            queryClient.invalidateQueries({ queryKey: ['shops'] });
            console.log('Featured offerings updated successfully!');
        },
        onError: (error) => {
            console.error('Failed to update featured offerings:', error);
        },
    });
};


// ... (Existing imports and hooks) ...
import { getShopPageDataBySlug } from '../store/services/shopService'; // <-- NEW IMPORT

/**
 * NEW: Custom hook to fetch shop data required for the public-facing business page.
 * @param {string} slug The shop's unique URL slug.
 * @returns {{shopData: object, isLoading: boolean, isError: boolean}}
 */
export const useShopPageData = (slug) => {
    return useQuery({
        queryKey: ['shopPageData', slug],
        queryFn: () => getShopPageDataBySlug(slug),
        enabled: !!slug,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

/////////////////////// Shop Editors ///////////////////////////////////
export const useGeneralBrandingUpdate = () => {
    // 1. Get the necessary mutations
    const updateShopMutation = useUpdateShop();
    const uploadFileMutation = useUploadFile();
    const queryClient = useQueryClient();
    
    // Get the setter for Zustand
    const setCurrentShop = useShopStore((state) => state.setCurrentShop);

    /**
     * @param {{id: string, updatePayload: object, newLogoFile: File | null, userId?: string}} payload 
     */
    const mutate = async ({ id, updatePayload, newLogoFile, userId }) => {
        if (!id) {
            throw new Error("Shop ID is required for update.");
        }

        const finalPayload = { ...updatePayload };

        // --- Handle Logo Upload ---
        if (newLogoFile) {
            console.log("Uploading new logo...");
            
            const logoUrl = await uploadFileMutation.mutateAsync({ 
                file: newLogoFile, 
                userId,
                shopId: id, 
            });
            
            finalPayload.shop_logo_url = logoUrl;
        }
        
        // --- Execute Main Shop Update ---
        await updateShopMutation.mutateAsync({ 
            id: id, 
            payload: finalPayload 
        });
        
        // 1. Manual cache invalidation
        await queryClient.invalidateQueries({ queryKey: ['shop', id] });
        
        // 2. FIX: Force fetch the fresh data immediately from the API, 
        //        EXPLICITLY providing the queryFn.
        const updatedShopData = await queryClient.fetchQuery({ 
            queryKey: ['shop', id], 
            queryFn: () => getShopById(id),
        });

        // 3. Sync the fresh data with the Zustand store
        if (updatedShopData) {
            setCurrentShop(updatedShopData);
            console.log("Zustand store synchronized with fresh shop data.");
        }

        // 4. Invalidate the list of all shops
        queryClient.invalidateQueries({ queryKey: ['shops'] });
    };

    return { 
        mutate, 
        isPending: updateShopMutation.isPending || uploadFileMutation.isPending, 
        isSuccess: updateShopMutation.isSuccess,
        isError: updateShopMutation.isError || uploadFileMutation.isError,
    };
};

// src/hooks/useShops.js (or new useShopUpdater.js)


// ... other useQuery hooks for shops

export const useUpdateShopLocationContact = () => {
    const queryClient = useQueryClient();
    const setCurrentShop = useShopStore(state => state.setCurrentShop);

    return useMutation({
        mutationFn: ({ shopId, updatedLocation, updatedContact }) =>
            updateShopLocationAndContact(shopId, updatedLocation, updatedContact),

        onSuccess: (updatedShop) => {
            // 1. Update the Zustand store's currentShop state
            setCurrentShop(updatedShop);

            // 2. Invalidate the 'currentShop' query to refetch fresh data or update cache
            queryClient.invalidateQueries({ queryKey: ['shop', updatedShop.id] });
            queryClient.invalidateQueries({ queryKey: ['currentShop'] });

            toast.success("Location and Contact updated successfully!");
        },

        onError: (error) => {
            console.error("Update failed:", error);
            toast.error("Failed to save changes. Please try again.");
        },
    });
};



// src/hooks/useShops.js


export const useUpdateShopHours = () => {
    const queryClient = useQueryClient();
    const setCurrentShop = useShopStore(state => state.setCurrentShop);

    return useMutation({
        // 🚨 FIX APPLIED HERE: The function must be available via import
        mutationFn: ({ shopId, updatedHours }) =>
            updateShopHours(shopId, updatedHours), // Line 335 (or similar) will now work

        onSuccess: (updatedShop) => {
            // 1. Update the Zustand store's currentShop state
            setCurrentShop(updatedShop);

            // 2. Invalidate the 'currentShop' query to refetch fresh data or update cache
            queryClient.invalidateQueries({ queryKey: ['shop', updatedShop.id] });
            queryClient.invalidateQueries({ queryKey: ['currentShop'] });

            toast.success("Business hours updated successfully!");
        },

        onError: (error) => {
            console.error("Hours update failed:", error);
            toast.error("Failed to save hours. Please try again.");
        },
    });
};


export const useUpdateShopPolicies = () => {
    const queryClient = useQueryClient();
    const setCurrentShop = useShopStore(state => state.setCurrentShop); // Action to update Zustand store

    return useMutation({
        mutationFn: ({ shopId, updatedPolicies }) =>
            updateShopPolicies(shopId, updatedPolicies),

        onSuccess: (updatedShop) => {
            
            // 🚨 CRITICAL FIX: Transform the updatedShop data structure.
            // Supabase returns policy data nested under the 'policies' key.
            // We need to move these fields to the top level to match the 
            // expected structure of the 'currentShop' object in Zustand/component logic.
            const { policies, ...restOfShop } = updatedShop;

            const normalizedShopForStore = {
                ...restOfShop,
                ...(policies || {}), // Spread policies fields (return_policy, payment_options, etc.) to the top level
            };

            // 1. Update the Zustand store's currentShop state with the normalized data
            setCurrentShop(normalizedShopForStore); 

            // 2. Invalidate relevant queries to ensure data freshness across the app
            queryClient.invalidateQueries({ queryKey: ['shop', updatedShop.id] });
            queryClient.invalidateQueries({ queryKey: ['currentShop'] });

            toast.success("Policies and terms updated successfully! 🚀");
        },

        onError: (error) => {
            console.error("Policies update failed:", error);
            toast.error("Failed to save policies. Please try again.");
        },
    });
};




export const useUpdateShopDelivery = () => {
    const queryClient = useQueryClient();
    const setCurrentShop = useShopStore(state => state.setCurrentShop);

    return useMutation({
        mutationFn: ({ shopId, updatedDelivery }) =>
            updateShopDeliveryZones(shopId, updatedDelivery),

        onSuccess: (updatedShop) => {
            
            // 🚨 CRITICAL: Extract the updated delivery data from the nested page_layout_config
            let deliveryQuarters = [];
            // Use the flag sent from the UI (client state) since it's not saved to the DB
            let isDeliveryEnabled = true; // Assume if saving worked, the user intends for it to be enabled

            const deliveryComponent = updatedShop.page_layout_config?.find(
                comp => comp.component === 'TimingsDeliverySection'
            );

            if (deliveryComponent) {
                // Extract the updated array from the component props
                deliveryQuarters = deliveryComponent.props?.deliveryQuarters || [];
                // If the component is present, we consider it enabled for UI purposes
                isDeliveryEnabled = true; 
            } else {
                 isDeliveryEnabled = false; // If the component was removed/not found, consider it disabled
            }
            
            // Create a normalized shop object to update the Zustand store (flatten the data)
            const normalizedShopForStore = {
                ...updatedShop,
                delivery_quarters: deliveryQuarters,
                isDeliveryEnabled: isDeliveryEnabled, 
            };

            setCurrentShop(normalizedShopForStore); 
            queryClient.invalidateQueries({ queryKey: ['shop', updatedShop.id] });
            queryClient.invalidateQueries({ queryKey: ['currentShop'] });

            toast.success("Delivery zones updated successfully! 🚚");
        },

        onError: (error) => {
            console.error("Delivery zone update failed:", error);
            toast.error("Failed to save delivery zones. Please try again.");
        },
    });
};
// ------------------------- For Admin Edit Shops-------------------------



// Hook: Fetch paginated list of shops specifically for admin management
export const useAdminPaginatedShops = (page = 0) => {
    return useQuery({
        queryKey: ['admin-shops-list', page],
        queryFn: () => fetchPaginatedShopsForAdminList(page),
        keepPreviousData: true,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

// Hook: Fetch inventory based on the specific table required by the shop type
export const useShopInventoryData = (shopId, inventoryMode) => {
    return useQuery({
        queryKey: ['shop-inventory', shopId, inventoryMode],
        queryFn: () => {
            if (inventoryMode === "FOOD_AND_BEVERAGE") return fetchFoodAndBeverageInventoryByShopId(shopId);
            if (inventoryMode === "SERVICES") return fetchProfessionalServicesByShopId(shopId);
            if (inventoryMode === "VARIANTS") return fetchProductVariantsByShopId(shopId);
            // Default to variants for mixed mode or others
            return fetchProductVariantsByShopId(shopId);
        },
        enabled: !!shopId && !!inventoryMode,
    });
};





// ... (keep your existing hooks like useAllShops, etc.)

/**
 * Hook to update item name, category, or price
 */
export const useAdminUpdateItem = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, mode, updates }) => adminUpdateItemDetails(id, mode, updates),
        onSuccess: () => {
            queryClient.invalidateQueries(['shopInventory']);
            toast.success('Item updated successfully');
        },
        onError: (error) => toast.error(`Update failed: ${error.message}`)
    });
};

/**
 * Hook to delete an item from inventory
 */
export const useAdminDeleteItem = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, mode }) => adminDeleteItem(id, mode),
        onSuccess: () => {
            queryClient.invalidateQueries(['shopInventory']);
            toast.success('Item deleted');
        },
        onError: (error) => toast.error(`Delete failed: ${error.message}`)
    });
};

/**
 * Hook to create a new service or food item
 */
export const useAdminCreateItem = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ mode, payload }) => adminCreateNewInventoryItem(mode, payload),
        onSuccess: () => {
            queryClient.invalidateQueries(['shopInventory']);
            toast.success('New entry created!');
        },
        onError: (error) => toast.error(`Creation failed: ${error.message}`)
    });
};

export const useAdminUpdateShopCategories = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ shopId, categories }) => adminUpdateShopCategories(shopId, categories),
    onSuccess: (_, { shopId }) => {
      // Invalidate shop queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['shop', shopId] });
      queryClient.invalidateQueries({ queryKey: ['admin_shops'] });
    },
  });
};


// -------------------------------------------------------------
// src/hooks/useShops.js

// ... Your existing hooks remain completely untouched up here

/**
 * Hook to update Shop Core Settings (Business Style, Delivery/Home Service, Shop Type Category)
 */
export const useAdminUpdateShopConfig = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ shopId, currentShopType, currentServicePolicy, updates }) =>
            adminUpdateShopCoreConfig(shopId, { currentShopType, currentServicePolicy, updates }),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['shop', variables.shopId] });
            queryClient.invalidateQueries({ queryKey: ['shops'] });
            toast.success('Core configurations saved successfully!');
        },
        onError: (error) => {
            console.error('Error updating core configuration:', error);
            toast.error(`Failed to save adjustments: ${error.message}`);
        }
    });
};

/**
 * Hook to update Shop Operational Profile (Scale, POS, Inventory, etc.)
 */
export const useAdminUpdateShopOperationalProfile = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ shopId, currentOperationalProfile, updates }) =>
            adminUpdateShopOperationalProfile(shopId, { currentOperationalProfile, updates }),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['shop', variables.shopId] });
            queryClient.invalidateQueries({ queryKey: ['shops'] });
            toast.success('Operational metrics synchronized!');
        },
        onError: (error) => {
            console.error('Error updating operational profile:', error);
            toast.error(`Operational tracking crash: ${error.message}`);
        }
    });
};

// --------------------------Getting Menu Data From Tables----------------------------

// hooks/useShops.js

export const useShopServices = (shopId) => {
  return useQuery({
    queryKey: ['shop-services', shopId],
    queryFn: () => fetchServicesByShopId(shopId),
    enabled: !!shopId,
  });
};







// ---- APPEND THESE TO src/hooks/useShops.js ----
// (also add this import at the top of useShops.js, alongside the other shopService imports)
// import { fetchShopItemsForOnSaleToggle, bulkUpdateItemsOnSale } from '../store/services/shopService';

/**
 * Hook to fetch a shop's items (variants / services / food_menu_items),
 * normalized for the On-Sale toggle modal. Only fetches when the modal
 * is actually open (pass `enabled`).
 */
export const useShopItemsForOnSale = (shopId, businessStyle, enabled = true) => {
    return useQuery({
        queryKey: ['shopItemsOnSale', shopId, businessStyle],
        queryFn: () => fetchShopItemsForOnSaleToggle(shopId, businessStyle),
        enabled: !!shopId && !!businessStyle && enabled,
        staleTime: 1000 * 30,
    });
};

export const useBulkUpdateItemsOnSale = () => {
    const queryClient = useQueryClient();
    const setCurrentShop = useShopStore((s) => s.setCurrentShop);
    const currentShop = useShopStore((s) => s.currentShop);

    return useMutation({
        mutationFn: bulkUpdateItemsOnSale,
        onSuccess: (result, variables) => {
            queryClient.invalidateQueries({ queryKey: ['shopItemsOnSale'] });
            queryClient.invalidateQueries({ queryKey: ['shop-inventory'] });
            queryClient.invalidateQueries({ queryKey: ['shop'] });
            queryClient.invalidateQueries({ queryKey: ['shopPageData'] });
            if (currentShop && currentShop.id === variables.shopId && typeof result?.shopOnSale === 'boolean') {
                setCurrentShop({ ...currentShop, on_sale: result.shopOnSale });
            }
        },
    });
};

export const useUpdateShopOnSale = () => {
    const queryClient = useQueryClient();
    const { setCurrentShop, currentShop } = useShopStore();

    return useMutation({
        mutationFn: updateShopOnSaleStatus,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['shop', data.shopId] });
            queryClient.invalidateQueries({ queryKey: ['shops'] });
            queryClient.invalidateQueries({ queryKey: ['shopItemsOnSale'] });
            queryClient.invalidateQueries({ queryKey: ['shop-inventory'] });
            queryClient.invalidateQueries({ queryKey: ['shopPageData'] });
            if (currentShop && currentShop.id === data.shopId) {
                setCurrentShop({ ...currentShop, on_sale: data.isOnSale });
            }
        },
    });
};
// -----------------------------------------------------

// ----------------------------------------------------------


export const useCheckShopName = () => {
  return useMutation({
    mutationFn: (shopName) => checkShopNameExists(shopName)
  });
};

export const useCreateShop = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, ownerId, logoFile, photoFiles }) => 
      createShop({ payload, ownerId, logoFile, photoFiles }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shops'] });
      queryClient.invalidateQueries({ queryKey: ['unapproved-shops'] });
    },
  });
};

export const useApproveShop = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ shopId, adminId }) => approveShop(shopId, adminId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unapproved-shops'] });
      queryClient.invalidateQueries({ queryKey: ['shops'] });
    },
  });
};

/**
 * Hook to toggle a shop's availability status between 'Active' and 'Offline'.
 * Reads/writes the shopSlice's currentShop directly — no extra fetch needed
 * since shop_availability_status already lives on that object.
 */
export const useUpdateShopAvailability = () => {
    const queryClient = useQueryClient();
    const setCurrentShop = useShopStore((state) => state.setCurrentShop);
    const currentShop = useShopStore((state) => state.currentShop);

    return useMutation({
        mutationFn: ({ shopId, newStatus }) => updateShopAvailabilityStatus(shopId, newStatus),
        onSuccess: (updatedShop) => {
            // Keep the Zustand store in sync so the toggle button (and
            // anything else reading currentShop) reflects the new state
            // immediately, without waiting on a refetch.
            if (currentShop && currentShop.id === updatedShop.id) {
                setCurrentShop({
                    ...currentShop,
                    shop_availability_status: updatedShop.shop_availability_status,
                });
            }

            queryClient.invalidateQueries({ queryKey: ['shop', updatedShop.id] });
            queryClient.invalidateQueries({ queryKey: ['shops'] });
            queryClient.invalidateQueries({ queryKey: ['shopPageData'] });

            toast.success(
                updatedShop.shop_availability_status === 'Active'
                    ? 'Your shop is live again!'
                    : 'Your shop is now offline for maintenance.'
            );
        },
        onError: (error) => {
            console.error('Failed to update shop availability:', error);
            toast.error(error.message || 'Failed to update shop status. Please try again.');
        },
    });
};
export const useUpdateShopAutoAcceptConfig = () => {
    const queryClient = useQueryClient();
    const setCurrentShop = useShopStore((state) => state.setCurrentShop);
    const currentShop = useShopStore((state) => state.currentShop);

    return useMutation({
        mutationFn: ({ shopId, updates }) => updateShopAutoAcceptConfig(shopId, updates),
        onSuccess: (updatedShop) => {
            if (currentShop && currentShop.id === updatedShop.id) {
                setCurrentShop({
                    ...currentShop,
                    auto_accept_enabled: updatedShop.auto_accept_enabled,
                    auto_accept_reputation_levels: updatedShop.auto_accept_reputation_levels,
                    appointment_buffer_minutes: updatedShop.appointment_buffer_minutes,
                });
            }
            queryClient.invalidateQueries({ queryKey: ['shop', updatedShop.id] });
            queryClient.invalidateQueries({ queryKey: ['shops'] });
            toast.success('Auto-accept settings saved.');
        },
        onError: (error) => {
            console.error('Failed to update auto-accept config:', error);
            toast.error(error.message || 'Failed to save auto-accept settings.');
        },
    });
};

// src/hooks/useShops.js — add this hook (and import getBusinessPaymentInfo
// alongside your other shopService imports at the top of the file)
export const useBusinessPaymentInfo = (shopId) => {
    return useQuery({
        queryKey: ['businessPaymentInfo', shopId],
        queryFn: () => getBusinessPaymentInfo(shopId),
        enabled: !!shopId,
    });
};


export const useUpdateDeliveryPolicy = () => {
    const queryClient = useQueryClient();
    const setCurrentShop = useShopStore((state) => state.setCurrentShop);
    const currentShop = useShopStore((state) => state.currentShop);

    return useMutation({
        mutationFn: ({ shopId, servicePolicy }) =>
            updateShopDeliveryPolicy(shopId, servicePolicy),
        onSuccess: (updatedShop) => {
            if (currentShop && currentShop.id === updatedShop.id) {
                setCurrentShop({
                    ...currentShop,
                    service_policy: updatedShop.service_policy,
                });
            }

            queryClient.invalidateQueries({ queryKey: ['shop', updatedShop.id] });
            queryClient.invalidateQueries({ queryKey: ['shops'] });
            queryClient.invalidateQueries({ queryKey: ['shopPageData'] });

            toast.success('Politique de livraison mise à jour avec succès !');
        },
        onError: (error) => {
            console.error('Failed to update delivery policy:', error);
            toast.error(
                error.message || 'Erreur lors de la mise à jour de la politique.'
            );
        },
    });
};